import React, { useState, useEffect, useRef } from "react";

// Revenue value options
const revenueOptions = [
  { label: "0.1M", value: 100000 },
  { label: "0.5M", value: 500000 },
  { label: "1M", value: 1000000 },
  { label: "5M", value: 5000000 },
  { label: "10M", value: 10000000 },
  { label: "25M", value: 25000000 },
  { label: "50M", value: 50000000 },
  { label: "100M", value: 100000000 },
  { label: "1B", value: 1000000000 },
  { label: "5B", value: 5000000000 },
  { label: "10B", value: 10000000000 },
];

// Hook for outside click
function useClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) return;
      handler(event);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}

// Sub dropdown for Min / Max
function DropdownSelector({ placeholder, value, setValue, options }) {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);

  const display = value
    ? options.find((o) => o.value === value)?.label
    : placeholder;

  const handleSelect = (val) => {
    setValue(val);
    setOpen(false);
  };

  const dropdownStyle = {
    background: "#fff",
    border: focused ? "1px solid #2563EB" : "1px solid #CEE0F3",
    borderRadius: "6px",
    padding: "0.5rem 0.75rem",
    cursor: "pointer",
    fontFamily: "Inter, sans-serif",
    fontWeight: 400,
    // fontSize: "0.875rem",
    fontSize: "12.8px",
    color: "#6c7681ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    transition: "all 0.15s ease",
    boxShadow: focused ? "0 0 0 3px rgba(37,99,235,0.15)" : "none",
  };

  const menuStyle = {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    background: "#fff",
    border: "1px solid #E2E8F0",
    borderRadius: "6px",
    marginTop: "4px",
    boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
    maxHeight: "200px",
    overflowY: "auto",
    zIndex: 100,
  };

  const optionStyle = (selected) => ({
    padding: "0.5rem 0.75rem",
    cursor: "pointer",
    background: selected ? "#F8FAFC" : "#fff",
    color: "#6c7681ff",
    fontFamily: "Inter, sans-serif",
    fontWeight: 400,
    // fontSize: "0.875rem",
    fontSize: "12.8px",
  });
  const finalOptions = [{ label: "Select", value: null }, ...options];
  return (
    <div style={{ position: "relative", flex: 1 }}>
      <div
        style={dropdownStyle}
        onClick={() => setOpen(!open)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        tabIndex={0}
      >
        <span>{display}</span>
        {/* <input type="text" value={display} readOnly /> */}

        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          viewBox="0 0 24 24"
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>

      {open && (
        <div style={menuStyle}>
          
          {finalOptions.map((opt) => {
            const isSelected = value === opt.value;
            return (
              <div
                key={opt.value}
                onClick={() => handleSelect(opt.value)}
                style={optionStyle(isSelected)}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#F1F5F9")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = isSelected ? "#F8FAFC" : "#fff")
                }
              >
                {opt.label}
              </div>
            );
          })}

        </div>
      )}
    </div>
  );
}

export default function RevenueSelector({ orgRevenue, setOrgRevenue }) {
  const [isOpen, setIsOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const ref = useRef();

  useClickOutside(ref, () => setIsOpen(false));

  const minOptions = revenueOptions;
  const maxOptions = revenueOptions.filter(
    (o) => orgRevenue[0] == null || o.value > orgRevenue[0]
  );

  const minLabel =
    orgRevenue[0] &&
    revenueOptions.find((o) => o.value === orgRevenue[0])?.label;
  const maxLabel =
    orgRevenue[1] &&
    revenueOptions.find((o) => o.value === orgRevenue[1])?.label;

  const displayLabel =
    minLabel || maxLabel
      ? `${minLabel || "Min"}, ${maxLabel || "Max"}`
      : "Company Revenue";

  const mainDropdownStyle = {
    background: "#fff",
    border: focused ? "1px solid #2563EB" : "1px solid #CEE0F3",
    borderRadius: "6px",
    padding: "0.6rem 0.5rem",
    cursor: "pointer",
    fontFamily: "Inter, sans-serif",
    fontWeight: 400,
    // fontSize: "0.875rem",
    fontSize: "12.8px",
    color: "#6c7681ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    transition: "all 0.15s ease",
    boxShadow: focused ? "0 0 0 3px rgba(37,99,235,0.15)" : "none",
    position: "relative",
  };

  const panelStyle = {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    background: "#fff",
    border: "1px solid #E2E8F0",
    borderRadius: "6px",
    marginTop: "4px",
    padding: "0.75rem",
    boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
    zIndex: 100,
  };

  return (
    <div ref={ref} style={{ position: "relative", width: "100%" }}>
      {/* Main dropdown header */}
      <div
        style={mainDropdownStyle}
        onClick={() => setIsOpen(!isOpen)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        tabIndex={0}
      >
        <span>{displayLabel}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          viewBox="0 0 24 24"
          style={{
            position: "absolute",
            right: "0.25rem",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",         
          }}
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>

      {isOpen && (
        <div style={panelStyle}>
          <div style={{ display: "flex", gap: "1rem" }}>
            <DropdownSelector
              placeholder="Min"
              value={orgRevenue[0]}
              setValue={(val) => {
                if (val === null) {
                  // Reset min to default
                  const [, max] = orgRevenue;
                  setOrgRevenue(max !== undefined ? [undefined, max] : []);
                  return;
                }
                let newMax = orgRevenue[1];
                if (newMax !== undefined && newMax <= val) newMax = undefined;
                setOrgRevenue([val, newMax]);

              }}
              options={minOptions}
            />
            <DropdownSelector
              placeholder="Max"
              value={orgRevenue[1]}
              setValue={(val) => {
                if (val === null) {
                  // Reset max to default
                  const [min] = orgRevenue;
                  setOrgRevenue(min !== undefined ? [min] : []);
                  return;
                }
                const [min] = orgRevenue;
                setOrgRevenue([min, val]);
              }}
              options={maxOptions}
            />
          </div>
        </div>
      )}
    </div>
  );
}
